import { Component, AfterViewInit, OnInit, ElementRef, ViewChild, TemplateRef, Renderer2 } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { UserService } from './user.service';
import { GlobalErrorHandler } from '../shared/core/error-handler';
import { NotificationsService, Notification } from '../shared/helper-components/index';
import { IUser } from './user.model';
import { AppService } from '../app.service';
import { GenericValidator } from "../shared/generic-validator";
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';


@Component({
  selector: 'add-user',
  standalone: false,
  templateUrl: './add-user.component.html',
  styleUrls: ['./add-user.component.css']
})
export class AddUserComponent implements AfterViewInit, OnInit {
  @ViewChild('addUserDialog', { static: true }) addUserDialog: TemplateRef<any>;
  @ViewChild('userFirstNameInput', { static: false }) userFirstNameInput: ElementRef;
  userRole: string = "Production";
  private dialogRef: MatDialogRef<any>;
  userForm : FormGroup;
  private validationMessages: { [key: string]: { [key: string]: string } };
  private genericValidator: GenericValidator;
  displayMessage: { [key: string]: string } = {};
  constructor(
    private _userService: UserService,
    private _errorHandler: GlobalErrorHandler,
    private _notificationsService: NotificationsService,
    private _appService: AppService,
    private fb: FormBuilder,
    private _dialog: MatDialog,
    private renderer: Renderer2,
    private el: ElementRef
  ){
    this.validationMessages = {
      userFirstName: {
          required: 'Please enter the user first name.',
          pattern: 'Please enter the user first name.'
      },
      userLastName: {
        required: 'Please enter the user last name.',
        pattern: 'Please enter the user last name.'
      },
      userEmail: {
        required: 'Please enter the user email.',
        pattern: 'Please enter a valid email.'
      },
    };
    this.genericValidator = new GenericValidator(this.validationMessages);
  }
 
  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    if (!this.userForm){
      this.userForm = this.fb.group({
        userRole: '',
        userFirstName: ['', [Validators.required, Validators.pattern(/.*[^ ].*/)]],
        userLastName: ['', [Validators.required, Validators.pattern(/.*[^ ].*/)]],
        userEmail: ['', [Validators.required, Validators.pattern(/^([a-zA-Z0-9_\-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$/)]],
        readonlyUser: [false],
        secondApproval: [false]
      });
    }
    else{
      this.userForm.controls.userFirstName.reset('');
      this.userForm.controls.userLastName.reset('');
      this.userForm.controls.userEmail.reset('');
    }
    this.userRole = "Production";
    this.userForm.controls.readonlyUser.setValue(false);
    this.userForm.controls.secondApproval.setValue(false);
  }

  ngAfterViewInit() {
    // Add bounceInUp animation via native DOM
    const mainContainer = this.el.nativeElement.querySelector('.main-container');
    if (mainContainer) {
      this.renderer.addClass(mainContainer, 'animated');
      this.renderer.addClass(mainContainer, 'bounceInUp');
      mainContainer.addEventListener('animationend', () => {
        this.renderer.removeClass(mainContainer, 'animated');
        this.renderer.removeClass(mainContainer, 'bounceInUp');
      }, { once: true });
    }

    // Set tabindex on form elements
    const formElements = this.el.nativeElement.querySelectorAll('input, select, textarea, button');
    formElements.forEach((el: HTMLElement) => {
      this.renderer.setAttribute(el, 'tabindex', '1');
    });
    const pickerElements = this.el.nativeElement.querySelectorAll('.picker, .picker__input');
    pickerElements.forEach((el: HTMLElement) => {
      this.renderer.setAttribute(el, 'tabindex', '-1');
    });

    // Keep lightweight live feedback while typing.
    this.userForm.valueChanges
      .pipe(debounceTime(300))
      .subscribe(() => {
        this.displayMessage = this.genericValidator.processMessages(this.userForm);
      });
  }

  onFieldBlur(controlName: string): void {
    const control = this.userForm && this.userForm.get(controlName);
    if (control) {
      control.markAsTouched();
    }
    this.displayMessage = this.genericValidator.processMessages(this.userForm);
  }

  optionNewUserRole(role: string){
    this.userRole = role;
  }

  openAddUserModal(): void {
      this.dialogRef = this._dialog.open(this.addUserDialog, {
        disableClose: true,
        width: '751px',
        height: '540px',
        maxWidth: '95vw',
        maxHeight: '85vh',
        position: { top: '90px' },
        panelClass: 'users-dialog'
      });
      setTimeout(() => {
        if (this.userFirstNameInput) {
          this.userFirstNameInput.nativeElement.focus();
        }
      });
  }

  cancelAddUser(): void {
    this.initForm();
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }
  
  saveUser(): void {
    if (this.userForm.dirty && this.userForm.valid) {
      let secondApproval = this.userRole == 'Clearance' ? this.userForm.controls.secondApproval.value : false;
      let newuser: IUser = {
        userName: this.userForm.controls.userEmail.value,
        email: this.userForm.controls.userEmail.value,
        userRoleName: 'SCRIMS ' + this.userRole,
        name: this.userForm.controls.userFirstName.value.trim() + ' ' + this.userForm.controls.userLastName.value.trim(),
        readonly: this.userForm.controls.readonlyUser.value,
        secondApproval: secondApproval
      }
      this._userService.getUser(this.userForm.controls.userEmail.value)
      .subscribe( (user: any) => {
        if (user==null){
          this._userService.addUser(newuser)
          .subscribe(
            (p) => {
              this.onSaveComplete(p);
              if (this.dialogRef) {
                this.dialogRef.close();
              }
            },
            (error: any) => {
              this._errorHandler.processError(error);
            }
          );
        }
        else{
          let userToUpdate = <IUser>{
            userName: user.UserName,
            name: user.Name,
            email: user.Email,
            userRoleName: user.UserRoleName,
            readonly: this.userForm.controls.readonlyUser.value,
            secondApproval: secondApproval
            };
          if (userToUpdate.userRoleName == 'SCRIMS Production' || userToUpdate.userRoleName == 'SCRIMS Clearance'){
          this._notificationsService.add(new Notification('error', `A user with email ${this.userForm.controls.userEmail.value} already exists.`));
          }
          else{
            userToUpdate.userRoleName = 'SCRIMS ' + this.userRole;
            userToUpdate.name = this.userForm.controls.userFirstName.value + ' ' + this.userForm.controls.userLastName.value;
            this._userService.patchUser(userToUpdate)
              .subscribe(
                (p) => {
                  this.onSaveComplete(p);
                  if (this.dialogRef) {
                    this.dialogRef.close();
                  }
                },
                (error: any) => {
                  this._errorHandler.processError(error);
                }
              );
          }
        }
      },
      (error: any)=>{
        this._errorHandler.processError(error);
      });
    
    }
    else if (!this.userForm.valid){
      var fieldsNotValid = ''.concat(
          this.userForm.controls.userFirstName.valid ? "" : "First Name|",
          this.userForm.controls.userLastName.valid ? "" : "Last Name|",
          this.userForm.controls.userEmail.valid ? "" : "Email|")
        .split("|");
      fieldsNotValid.splice(-1);
      var validationMessage = `${fieldsNotValid.join(", ")} ${fieldsNotValid.length <=1 ? " is " : " are "} required.`;
      this._notificationsService.add(new Notification('error', validationMessage));  
    }
  }

  onSaveComplete(saveCompleted): void {
    if (!saveCompleted){
      this._notificationsService.add(new Notification('error', `User with email ${this.userForm.controls.userEmail.value} could not be added`));
      return
    }
    var newUserEmail = this.userForm.controls.userEmail.value;
    this._appService.sendNewUserEmail(newUserEmail).subscribe(result => {
      if (result)
        this._notificationsService.add(new Notification('success', `User with email ${this.userForm.controls.userEmail.value} was added and notified successfully`));
      else
        this._notificationsService.add(new Notification('warn', `User with email ${this.userForm.controls.userEmail.value} was added but could NOT be notified by email`));
      this.initForm();
      this.userForm.markAsPristine();
    });
  }
}